import React, { useEffect, useRef, useState, useCallback } from "react";
import { Document } from "@langchain/core/documents";
import ChatLayout from "./ChatLayout";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import LoadingSpinner from "../../icons/spinner";
import { websocket_endpoint, websocket_voice_endpoint } from "../../service/apiService";
import { getDisplayFileName } from "../../hooks/useFileUpload";
import { useSession } from "../../context/SessionContext";
import { useUser } from "../../context/UserContext";
import { useModel } from "../../context/ModelContext";
import { useHat } from "../../context/HatContext";
import { useKnowledgeBase } from "../../context/KnowledgeBaseContext";
import { useWorkflow } from "../../context/WorkflowContext";
import { useBrainBankSync } from "../../context/BrainBankSyncContext";
import { useComposerBrainBankSync } from "../../hooks/useComposerBrainBankSync";
import { useInternet } from "../../context/InternetContext";
import { useAuth } from "../../context/AuthContext";
import NovaVoiceManager, { isVoiceModel } from "../../utils/NovaVoiceManager";
import VoiceModeModal from "../VoiceMode/VoiceModeModal";
import {
  EMPTY_VOICE_BRAINBANK_STATE,
  VoiceBrainBankState,
  VoiceTranscriptEntry,
} from "../../types";
import { getAllClientLogos, generateClientLogoDownloadUrl, loadConversation as fetchConversation } from "../../service/apiService";

export type MessageContent = {
  content_type: string;
  parts: string;
  meta: {
    files: any[];
    audio_url?: string;
    quotedText?: string;
    quotedMessageId?: string;
  };
};

// ... (skipping unchanged lines)



export type Message = {
  id: string;
  createdAt: Date;
  content: MessageContent;
  role: "user" | "assistant";
  sources?: Document[];
};

// Add interface for client logo data
interface ClientLogoData {
  id: string;
  file_name: string;
  status: string;
  is_current: boolean | string;
  version: number;
  created_at: string;
}

const ChatWindow = (props: {
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isExpanded: boolean;
}) => {
  const {
    sessionId,
    sessions, // Get sessions from context
    setSessionId,
    setSessions, // NEW: Get setSessions to update history manually
    createSessionId,
    updateSessionTitle,
    loadSessionConversation,
  } = useSession();

  const { userId, token, userDetails } = useUser();
  const { selectedModel, setSelectedModel, getVoiceModel, getDefaultModel, models, effort } = useModel();
  const { selectedHat } = useHat();
  const { selectedKnowledgeBase, markKnowledgeBasePinned, restoreSessionKnowledgeBase } = useKnowledgeBase();
  const { selectedWorkflow } = useWorkflow();
  const { syncAttachments } = useBrainBankSync();
  const { mode: brainBankSyncMode, brainBank: syncBrainBank } = useComposerBrainBankSync();
  const { internetEnabled } = useInternet();
  const { reAuthenticate } = useAuth();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialMessage = searchParams.get("q");

  const [isReady, setIsReady] = useState(true);
  const [chatHistory, setChatHistory] = useState<[string, string][]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesRef = useRef<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [browsing, setBrowsing] = useState(false);
  const [messageAppeared, setMessageAppeared] = useState(false);
  const [focusMode, setFocusMode] = useState("webSearch");
  const [micError, setMicError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [showVACreator, setShowVACreator] = useState(false);
  const [activeConnector, setActiveConnector] = useState<string | undefined>(undefined);
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(false);

  // NEW: Add these state variables for real-time text display
  const [userText, setUserText] = useState("");
  const [assistantText, setAssistantText] = useState("");
  const [conversationHistory, setConversationHistory] = useState<VoiceTranscriptEntry[]>([]);
  const [voiceBrainBank, setVoiceBrainBank] = useState<VoiceBrainBankState>(EMPTY_VOICE_BRAINBANK_STATE);

  const wsRef = useRef<WebSocket | null>(null);
  const tokenRef = useRef<string>(token || "");
  const novaVoiceManagerRef = useRef<NovaVoiceManager | null>(null);
  // Add client logo state
  const [clientLogoUrl, setClientLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [logoError, setLogoError] = useState(false);

  // NEW: Add local state for full conversation audio
  const [fullAudioUrl, setFullAudioUrl] = useState<string | null>(null);

  // Get full conversation audio URL if available
  const currentSession = sessions.find(s => s.id === sessionId);
  
  useEffect(() => {
    const sessionAudioUrl = currentSession?.files?.find((f: any) => f.full_conversation_audio_url)?.full_conversation_audio_url || null;
    setFullAudioUrl(sessionAudioUrl);
  }, [currentSession]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    tokenRef.current = token || "";
  }, [token]);

  // Add client logo fetch logic (similar to connector pattern)
  const fetchClientLogo = useCallback(async () => {
    try {
      setLogoLoading(true);
      setLogoError(false);

      console.log('🔍 Fetching client logos...');

      const allLogosResponse = await getAllClientLogos();

      if (allLogosResponse && allLogosResponse.data && allLogosResponse.data.length > 0) {
        const logos = allLogosResponse.data;

        // Filter only active logos
        const activeLogos: ClientLogoData[] = logos.filter((logo: ClientLogoData) => logo.status === 'active');

        if (activeLogos.length > 0) {
          // Sort by version (highest first) and then by created_at (newest first)
          const sortedLogos: ClientLogoData[] = activeLogos.sort((a: ClientLogoData, b: ClientLogoData) => {
            if (b.version !== a.version) {
              return b.version - a.version;
            }
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          });

          const latestLogo: ClientLogoData = sortedLogos[0];

          try {
            const downloadResponse = await generateClientLogoDownloadUrl(latestLogo.id);

            if (downloadResponse && downloadResponse.presigned_url) {
              setClientLogoUrl(downloadResponse.presigned_url);
              console.log('✅ Client logo URL set successfully!');
            } else {
              console.warn('❌ No presigned URL received for latest logo');
              setLogoError(true);
            }
          } catch (downloadError) {
            console.error('❌ Failed to get logo download URL:', downloadError);
            setLogoError(true);
          }
        } else {
          console.log('❌ No active logos found');
          setClientLogoUrl(null);
        }
      } else {
        console.log('❌ No logos found');
        setClientLogoUrl(null);
      }
    } catch (fetchError) {
      console.error('❌ Failed to fetch client logos:', fetchError);
      setLogoError(true);
    } finally {
      setLogoLoading(false);
    }
  }, []);

  // Fetch client logo once when component mounts (like connector pattern)
  useEffect(() => {
    fetchClientLogo();
  }, [fetchClientLogo]);

  const loadConversation = useCallback(
    async (sessionId: string) => {
      try {
        const conversation = await loadSessionConversation(sessionId);
        if (conversation && conversation.length > 0) {
          const mergedMessages: Message[] = conversation.map((conv) => ({
            id: conv.id,
            createdAt: new Date(conv.created_at),
            content: {
              content_type: conv.content.content_type,
              parts: conv.content.parts,
              meta: conv.content.meta,
            },
            role: conv.author === "user" ? "user" : "assistant",
          }));
          setMessages(mergedMessages);
        }
      } catch (error) {
        console.error("Failed to load conversation:", error);
      }
    },
    [loadSessionConversation]
  );

  // A voice conversation's session is new and empty when the page switches to
  // it, so the usual load (and its "Conversation loaded" toast) is skipped.
  // Skipped once: coming back to that chat later loads it as usual.
  const skipLoadForSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId) return;
    if (sessionId === skipLoadForSessionRef.current) {
      skipLoadForSessionRef.current = null;
      return;
    }
    loadConversation(sessionId);
  }, [sessionId, loadConversation]);

  // The conversation open on the page right now, for callbacks that land late
  // (a voice save confirmed after the user moved to another chat).
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  // Refresh a voice conversation from what the backend stored, without the
  // loading state or toast an ordinary conversation load shows: used after a
  // reconnect and when voice ends, while the user is still in the chat.
  const refreshVoiceConversation = useCallback(async (voiceSessionId: string) => {
    let conversation: any[] = [];
    try {
      conversation = (await fetchConversation(voiceSessionId)) || [];
    } catch {
      return;
    }
    if (!conversation.length) return;
    // The user may have moved to another chat while this was loading.
    if (sessionIdRef.current !== voiceSessionId) return;
    setMessages(
      conversation.map((conv: any) => ({
        id: conv.id,
        createdAt: new Date(conv.created_at),
        content: {
          content_type: conv.content.content_type,
          parts: conv.content.parts,
          meta: conv.content.meta,
        },
        role: conv.author === "user" ? "user" : "assistant",
      }))
    );
    // The voice window's transcript, only while that conversation is live in it.
    const manager = novaVoiceManagerRef.current;
    if (!manager || !manager.hasConversation() || manager.getSessionId() !== voiceSessionId) return;
    manager.setConversationHistory(
      conversation
        .filter((m: any) => m?.content?.parts)
        .map((m: any) => ({
          id: m.id,
          role: m.author === 'user' ? 'user' : 'assistant',
          text: m.content.parts,
          createdAt: m.created_at,
          interrupted: m.completion?.reason === 'human_interruption',
        }))
    );
  }, []);

  const isTokenExpired = () => {
    const expTime = sessionStorage.getItem('authTokenExp');
    if (!expTime) return false;

    const expTimestamp = parseInt(expTime) * 1000;
    const currentTime = Date.now();
    return currentTime >= expTimestamp;
  };

  // The voice connection URL. A reconnect adds the session, so the backend
  // resumes that conversation instead of starting a new one.
  const buildVoiceUrl = (ids: { hatId: string | null; knowledgeBaseId: string | null }, resumeSessionId?: string | null) => {
    // Tell the backend which voice model is selected; it falls back to the admin's voice default
    const voiceModelParam = isVoiceModel(selectedModel)
      ? `&model_id=${encodeURIComponent(selectedModel!.foundation_model_id)}`
      : '';
    const hatParam = ids.hatId ? `&hat_id=${encodeURIComponent(ids.hatId)}` : '';
    const knowledgeBaseParam = ids.knowledgeBaseId
      ? `&knowledge_base_id=${encodeURIComponent(ids.knowledgeBaseId)}`
      : '';
    const sessionParam = resumeSessionId ? `&session_id=${encodeURIComponent(resumeSessionId)}` : '';
    return `${websocket_voice_endpoint}?token=${encodeURIComponent(tokenRef.current)}${voiceModelParam}${hatParam}${knowledgeBaseParam}${sessionParam}`;
  };

  // The voice callbacks are wired once, so they read what they need from here
  // rather than from the render they were created in.
  const voiceLatestRef = useRef({
    restoreSessionKnowledgeBase,
    refreshVoiceConversation,
    buildVoiceUrl,
  });
  voiceLatestRef.current = { restoreSessionKnowledgeBase, refreshVoiceConversation, buildVoiceUrl };
  // Reconnect attempts since the connection was last up.
  const voiceReconnectAttemptsRef = useRef(0);
  // A BrainBank lookup can finish in a fraction of a second once the index is
  // cached. The status stays up at least this long, so the user sees that the
  // BrainBank was searched rather than a flicker.
  const SEARCH_STATUS_MIN_MS = 1200;
  const searchStartedAtRef = useRef(0);
  const searchClearTimerRef = useRef<number | null>(null);
  // A scheduled reconnect attempt, cancelled when the conversation ends.
  const reconnectTimerRef = useRef<number | null>(null);
  const clearVoiceTimers = () => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (searchClearTimerRef.current !== null) {
      window.clearTimeout(searchClearTimerRef.current);
      searchClearTimerRef.current = null;
    }
  };
  const clearVoiceTimersRef = useRef(clearVoiceTimers);
  // The voice conversation's own session, and the VA/BrainBank it started with.
  const voiceSessionRef = useRef<{ sessionId: string | null; hatId: string | null; knowledgeBaseId: string | null }>({
    sessionId: null,
    hatId: null,
    knowledgeBaseId: null,
  });

  // Initialize Nova Voice Manager
  useEffect(() => {
    novaVoiceManagerRef.current = new NovaVoiceManager();

    // Set up callbacks
    novaVoiceManagerRef.current.onStatusChange = (status, type) => {
      if (type === 'error') {
        setMicError(status);
      }
    };

    // Live transcript for the voice window
    novaVoiceManagerRef.current.onCurrentTextUpdate = (currentUserText, currentAssistantText, history) => {
      setUserText(currentUserText);
      setAssistantText(currentAssistantText);
      setConversationHistory([...history]);
    };

    // The conversation has its own session from the first moment: the chat
    // page switches to it, so every turn lands in it as it is saved and the
    // user is left in it, as an ordinary chat, when voice ends.
    novaVoiceManagerRef.current.onSessionCreated = (newSessionId) => {
      // The same session again: a reconnect went through. The page already
      // shows this conversation; catch up on anything saved during the drop.
      if (newSessionId === voiceSessionRef.current.sessionId) {
        voiceReconnectAttemptsRef.current = 0;
        setConnecting(false);
        voiceLatestRef.current.refreshVoiceConversation(newSessionId);
        return;
      }
      const { hatId, knowledgeBaseId } = voiceSessionRef.current;
      voiceSessionRef.current.sessionId = newSessionId;
      const now = new Date().toISOString();
      setMessages([]);
      skipLoadForSessionRef.current = newSessionId;
      setSessions((prev) =>
        prev.some((s) => s.id === newSessionId)
          ? prev
          : [
              {
                id: newSessionId,
                title: "Voice Conversation",
                createdAt: now,
                updatedAt: now,
                session_type: 'voice',
                hat_id: hatId,
                knowledge_base_id: knowledgeBaseId,
                files: [],
              } as any,
              ...prev,
            ]
      );
      setSessionId(newSessionId);
      // Carried over rather than cleared by the conversation switch; a picked
      // BrainBank is pinned to the session on the server already.
      voiceLatestRef.current.restoreSessionKnowledgeBase(newSessionId, knowledgeBaseId);
    };

    novaVoiceManagerRef.current.onMessageSaved = (saved) => {
      setMessages((prev) =>
        prev.some((m) => m.id === saved.messageId)
          ? prev
          : [
              ...prev,
              {
                id: saved.messageId,
                createdAt: new Date(saved.createdAt),
                content: { content_type: "text", parts: saved.text, meta: { files: [] } },
                role: saved.role === "user" ? "user" : "assistant",
              },
            ]
      );
      // The user has finished a turn: Kyva is working on the answer.
      if (saved.role === 'user') {
        setThinking(true);
      }
    };

    novaVoiceManagerRef.current.onInterrupted = () => {
      setIsPlaying(false);
    };

    // The connection dropped (or is being renewed before Nova Sonic's time
    // limit): resume the same conversation rather than ending it.
    novaVoiceManagerRef.current.onConnectionLost = (reason) => {
      const manager = novaVoiceManagerRef.current;
      const { sessionId: resumeId, hatId, knowledgeBaseId } = voiceSessionRef.current;
      if (!manager || !resumeId) return;
      setIsPlaying(false);
      setConnecting(true);
      // A search cut off by the drop never reports its end: clear it here.
      clearVoiceTimersRef.current();
      setThinking(false);
      setVoiceBrainBank((prev) => ({ ...prev, searching: false, searchKind: null, searchDocument: null }));

      const attempt = async () => {
        reconnectTimerRef.current = null;
        // Ended, or replaced by another conversation, while waiting to retry.
        if (voiceSessionRef.current.sessionId !== resumeId) return;
        voiceReconnectAttemptsRef.current += 1;
        if (voiceReconnectAttemptsRef.current > 3) {
          voiceReconnectAttemptsRef.current = 0;
          toast.error("The voice connection was lost. Your conversation is saved in this chat.");
          manager.endSession();
          return;
        }
        try {
          await manager.reconnect(
            voiceLatestRef.current.buildVoiceUrl({ hatId, knowledgeBaseId }, resumeId)
          );
        } catch (error) {
          console.error('Voice reconnect failed:', error);
          manager.endSession();
        }
      };
      // A renewal reconnects straight away; a drop backs off a little each time.
      const delay = reason === 'renewal' ? 0 : 500 * Math.max(0, voiceReconnectAttemptsRef.current);
      reconnectTimerRef.current = window.setTimeout(attempt, delay);
    };

    novaVoiceManagerRef.current.onBrainBankSelected = (brainBanks, pinned) => {
      setVoiceBrainBank((prev) => ({ ...prev, brainBanks, pinned }));
      const { sessionId: voiceSessionId } = voiceSessionRef.current;
      // Picked by voice: pin it in the chat too, so typing on afterwards keeps it.
      if (pinned && voiceSessionId && brainBanks[0]?.id) {
        voiceSessionRef.current.knowledgeBaseId = brainBanks[0].id;
        setSessions((prev) =>
          prev.map((s) => (s.id === voiceSessionId ? { ...s, knowledge_base_id: brainBanks[0].id } : s))
        );
        voiceLatestRef.current.restoreSessionKnowledgeBase(voiceSessionId, brainBanks[0].id);
      }
    };

    novaVoiceManagerRef.current.onBrainBankSearch = (status, info) => {
      if (searchClearTimerRef.current !== null) {
        window.clearTimeout(searchClearTimerRef.current);
        searchClearTimerRef.current = null;
      }
      if (status === 'started') {
        searchStartedAtRef.current = Date.now();
        setVoiceBrainBank((prev) => ({
          ...prev,
          searching: true,
          searchKind: info?.kind || 'brainbank',
          searchDocument: info?.document || null,
        }));
        setThinking(true);
        return;
      }
      const clear = () => {
        searchClearTimerRef.current = null;
        setVoiceBrainBank((prev) => ({ ...prev, searching: false, searchKind: null, searchDocument: null }));
      };
      const remaining = SEARCH_STATUS_MIN_MS - (Date.now() - searchStartedAtRef.current);
      if (remaining > 0) {
        searchClearTimerRef.current = window.setTimeout(clear, remaining);
      } else {
        clear();
      }
    };

    novaVoiceManagerRef.current.onError = (message) => {
      setMicError(message);
      toast.error(message, { duration: 6000 });
    };

    novaVoiceManagerRef.current.onAudioPlaybackStart = () => {
      setIsPlaying(true);
      setThinking(false);
    };

    novaVoiceManagerRef.current.onAudioPlaybackEnd = () => {
      setIsPlaying(false);
    };

    novaVoiceManagerRef.current.onSessionEnd = (sessionData) => {
      // The save of an ended conversation is confirmed in the background; by
      // then the user may already be in a new one, which must be left alone.
      const manager = novaVoiceManagerRef.current;
      // Connecting counts too: a new conversation may still be opening.
      const newerConversationLive = !!manager && manager.hasConversation();
      if (newerConversationLive) {
        if (sessionData?.sessionId) {
          setSessions((prev) =>
            prev.map((s) =>
              s.id === sessionData.sessionId ? { ...s, title: sessionData.title || s.title } : s
            )
          );
        }
        return;
      }

      clearVoiceTimersRef.current();
      // Reset all voice states when session ends
      setRecording(false);
      setMuted(false);
      setThinking(false);
      setIsPlaying(false);
      setConnecting(false);
      setUserText("");
      setAssistantText("");
      setConversationHistory([]);
      setVoiceBrainBank(EMPTY_VOICE_BRAINBANK_STATE);

      const voiceSessionId = sessionData?.sessionId || voiceSessionRef.current.sessionId;
      voiceSessionRef.current = { sessionId: null, hatId: null, knowledgeBaseId: null };
      if (!voiceSessionId) return;

      if (sessionData?.fullConversationAudioUrl) {
        setFullAudioUrl(sessionData.fullConversationAudioUrl);
      }
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== voiceSessionId) return s;
          const updatedFiles = s.files ? [...s.files] : [];
          if (sessionData?.fullConversationAudioUrl && !updatedFiles.some((f: any) => f.full_conversation_audio_url)) {
            updatedFiles.push({ full_conversation_audio_url: sessionData.fullConversationAudioUrl });
          }
          return {
            ...s,
            title: sessionData?.title || s.title,
            updatedAt: sessionData?.timestamp || s.updatedAt,
            files: updatedFiles,
          };
        })
      );
      // The stored conversation is the record: load it, so the page matches
      // what a refresh shows (including audio links signed for playback).
      voiceLatestRef.current.refreshVoiceConversation(voiceSessionId);

      if (sessionData && sessionIdRef.current === voiceSessionId) {
        toast.success("Voice conversation saved to this chat");
      }
    };

    return () => {
      clearVoiceTimersRef.current();
      if (novaVoiceManagerRef.current) {
        novaVoiceManagerRef.current.endSession();
      }
    };
  }, [setSessions, setSessionId]);

  // Starts the conversation on the first press; after that the mic stays live
  // and the button mutes and unmutes it.
  const handleMic = async () => {
    const voiceManager = novaVoiceManagerRef.current;
    if (!voiceManager) {
      console.error('Voice manager not initialized');
      return;
    }

    if (recording) {
      const nextMuted = !voiceManager.isMuted();
      voiceManager.setMuted(nextMuted);
      setMuted(nextMuted);
      return;
    }

    try {
      setConnecting(true);
      setMicError(null);

      // Start voice session if not already active
      if (!voiceManager.hasConversation()) {
        // The VA and BrainBank go along as they do with a typed message; a
        // workflow uses neither, so it clears both the same way. The backend
        // checks access to each before the conversation starts.
        const workflowId = selectedWorkflow?.id || null;
        const hatId = !workflowId ? selectedHat?.id || null : null;
        const knowledgeBaseId = !workflowId ? selectedKnowledgeBase?.id || null : null;
        voiceSessionRef.current = { sessionId: null, hatId, knowledgeBaseId };
        voiceReconnectAttemptsRef.current = 0;

        await voiceManager.startSession(buildVoiceUrl({ hatId, knowledgeBaseId }));
      }
      // End or X pressed while it was connecting: leave it ended.
      if (!voiceManager.hasConversation()) {
        setConnecting(false);
        return;
      }

      await voiceManager.startRecording();
      if (!voiceManager.hasConversation()) {
        setConnecting(false);
        return;
      }
      setRecording(true);
      setMuted(false);
      setConnecting(false);
    } catch (error) {
      console.error('Error starting voice recording:', error);
      setConnecting(false);
      // Show user-friendly error toast
      toast.error("Microphone access denied", {
        duration: 4000,
        style: {
          background: '#f7f7f7ff',
          color: '#7F22FE',
        },
      });
      setMicError('Unable to access microphone. Please check permissions.');
    }
  };

  const handleEndVoiceSession = () => {
    const voiceManager = novaVoiceManagerRef.current;
    // Also while reconnecting, so closing the window then cannot leave a
    // reconnect running in the background.
    if (voiceManager && voiceManager.hasConversation()) {
      voiceManager.endSession();
    }
  };

  // ---- Voice Mode window ---------------------------------------------------
  // Mounted here rather than in the empty-chat composer, so it stays open while
  // the page behind it fills with the conversation.
  const [showVoiceMode, setShowVoiceMode] = useState(false);
  // The model to go back to when voice ends: typing on in the same chat must
  // not go to a speech-only model.
  const modelBeforeVoiceRef = useRef<any>(null);

  const openVoiceMode = useCallback(() => {
    if (!isVoiceModel(selectedModel)) {
      const voiceModel = getVoiceModel();
      if (!voiceModel) {
        toast.error("Voice Mode is not available: no voice model is enabled.");
        return;
      }
      modelBeforeVoiceRef.current = selectedModel;
      setSelectedModel(voiceModel);
    }
    setShowVoiceMode(true);
  }, [selectedModel, getVoiceModel, setSelectedModel]);

  // The composer's voice button and the model picker both ask for Voice Mode
  // this way.
  useEffect(() => {
    const handleOpenVoiceMode = () => openVoiceMode();
    window.addEventListener('openVoiceMode', handleOpenVoiceMode);
    return () => window.removeEventListener('openVoiceMode', handleOpenVoiceMode);
  }, [openVoiceMode]);

  const closeVoiceMode = () => {
    handleEndVoiceSession();
    setShowVoiceMode(false);
    // The conversation is over for the user right now; the backend's
    // confirmation (title, recording) still lands when it arrives.
    setRecording(false);
    setMuted(false);
    setThinking(false);
    setIsPlaying(false);
    setConnecting(false);
    setUserText("");
    setAssistantText("");
    setConversationHistory([]);
    setVoiceBrainBank(EMPTY_VOICE_BRAINBANK_STATE);
    clearVoiceTimers();
    const previous = modelBeforeVoiceRef.current;
    modelBeforeVoiceRef.current = null;
    if (previous && !isVoiceModel(previous)) {
      setSelectedModel(previous);
    } else if (isVoiceModel(selectedModel)) {
      const fallback = getDefaultModel() || models.find((m) => m.is_enabled && !isVoiceModel(m));
      if (fallback && !isVoiceModel(fallback)) {
        setSelectedModel(fallback);
      }
    }
  };

  const sendMessage = async (message: string, uploadedFiles: File[] = [], connector?: string, quotedText?: string, quotedMessageId?: string) => {
    if (loading) return;
    setLoading(true);
    setAnalyzing(false);
    setBrowsing(false);
    setMessageAppeared(false);

    let sources: Document[] | undefined = undefined;
    let receivedMessage = "";
    let added = false;
    // Fire title generation at most once for THIS conversation. It is a local variable (not
    // component state) on purpose: the old shared `messagePairCompleted` flag was never reset,
    // so the 2nd+ new chat opened in the same browser session stayed stuck as "Untitled".
    let titleGenerated = false;

    let currentSessionId = sessionId;

    // "New conversation" = no messages exist yet in this chat. We must NOT key this off
    // `!sessionId`: attaching a file pre-creates the session (useFileUpload mints one to hold
    // the S3 upload) before the user ever sends, so `sessionId` is already set and the chat
    // would be wrongly treated as "resumed" and never get a title — leaving it stuck as
    // "Untitled Conversation". Message count is the reliable signal: 0 for a brand-new chat
    // (with or without an attached file) and >0 for a resumed session whose history is loaded.
    const isNewConversation = messagesRef.current.length === 0;

    if (!currentSessionId) {
      try {
        const modelId = selectedModel?.foundation_model_id
        const hatId = selectedHat?.id
        const title = "Untitled Conversation"
        currentSessionId = await createSessionId(modelId, hatId, title);
      } catch {
        setLoading(false);
        return;
      }
    }

    // Check if token is expired before establishing websocket connection
    let currentToken = tokenRef.current;
    if (isTokenExpired()) {
      console.log("Token expired before websocket connection, re-authenticating");
      try {
        // Start re-authentication
        const result = await reAuthenticate();
        if (!result.success) {
          toast.error("Authentication failed. Please try again.");
          setLoading(false);
          return;
        }

        // Wait for any in-progress token updates to complete
        await new Promise(resolve => setTimeout(resolve, 500));

        // Get the latest token from session storage
        const freshToken = sessionStorage.getItem("authToken");
        if (freshToken) {
          currentToken = freshToken;
          tokenRef.current = freshToken; // Update the ref with the new token
        } else {
          console.warn("No token found after re-authentication");
        }
      } catch (error) {
        console.error("Re-authentication error:", error);
        toast.error("Authentication failed. Please try again.");
        setLoading(false);
        return;
      }
    }

    // Always use the most current token for WebSocket
    const WS_URL = `${websocket_endpoint}?token=${encodeURIComponent(currentToken)}`;
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    // file_name is the minted stored name (must exactly match the S3 key basename
    // — the backend joins on it); original_file_name is what the user sees.
    const fileMetadata = uploadedFiles.map((file) => ({
      file_name: file.name,
      original_file_name: getDisplayFileName(file.name),
      file_size: file.size.toString(),
    }));

    ws.onopen = () => {
      // Implement mutual exclusivity: if workflow is selected, only send workflow_id
      const workflowId = selectedWorkflow?.id || null;
      const hatId = workflowId ? null : (selectedHat?.id || null);
      // The backend records the VA used on the session, and once the session
      // has a BrainBank keeps the first one, locking it to this conversation.
      // Mirror that locally so the navbar's VA box padlocks right away instead
      // of waiting for the next sessions refetch.
      if (hatId) {
        setSessions((prevSessions) =>
          prevSessions.some(
            (s) =>
              s.id === currentSessionId &&
              s.hat_id !== hatId &&
              (!s.hat_id || !s.knowledge_base_id)
          )
            ? prevSessions.map((s) =>
                s.id === currentSessionId ? { ...s, hat_id: hatId } : s
              )
            : prevSessions
        );
      }
      // A BrainBank rides alongside the VA rather than replacing it: with a VA
      // selected it stands in for that VA's own BrainBanks, for this chat only.
      // A workflow run reads neither, so it clears this the way it clears the VA.
      const knowledgeBaseId = workflowId ? null : (selectedKnowledgeBase?.id || null);
      // The backend records the first BrainBank used on the session the same
      // way. Mirror it too, so reopening this conversation before the next
      // sessions refetch still restores it.
      if (knowledgeBaseId) {
        setSessions((prevSessions) =>
          prevSessions.some((s) => s.id === currentSessionId && !s.knowledge_base_id)
            ? prevSessions.map((s) =>
                s.id === currentSessionId ? { ...s, knowledge_base_id: knowledgeBaseId } : s
              )
            : prevSessions
        );
      }

      const payload: any = {
        type: "message",
        data: message,
        session_id: currentSessionId,
        model_id: selectedModel?.foundation_model_id,
        meta: { files: fileMetadata, quotedText: quotedText, quotedMessageId: quotedMessageId },
        model_name: selectedModel?.display_name || selectedModel?.name,
        hat_id: hatId,
        knowledge_base_id: knowledgeBaseId,
        workflow_id: workflowId,
        internet_enabled: internetEnabled,
        long_term_memory_enabled: userDetails?.long_term_memory_enabled || false,
        short_term_memory_enabled: userDetails?.short_term_memory_enabled || false,
      };
      // Only for models with an Effort menu, and not for workflows (the menu is hidden
      // there); otherwise the server keeps the model's default.
      if (effort && !workflowId) {
        payload.effort = effort;
      }
      const currentConnector = connector || activeConnector;
      if (currentConnector) {
        payload.connector = currentConnector;
      }

      ws.send(JSON.stringify(payload));
    };

    // The server pins the first BrainBank a conversation uses; so does the
    // composer, which then stops offering a different one.
    if (!selectedWorkflow && selectedKnowledgeBase) {
      markKnowledgeBasePinned(selectedKnowledgeBase.id);
    }

    // Attachments also go to the conversation's BrainBank, one call per file;
    // the reply never waits on it. Started before the message is added below,
    // so the earlier-files banner never offers a file that is being sent now.
    if (brainBankSyncMode === "sync" && syncBrainBank && fileMetadata.length > 0) {
      syncAttachments(currentSessionId, fileMetadata, {
        id: syncBrainBank.id,
        name: syncBrainBank.name,
      });
    }
    // User message -> push to messages
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        content: {
          content_type: "text",
          parts: message,
          meta: { files: fileMetadata, quotedText: quotedText, quotedMessageId: quotedMessageId },
        },
        id: Math.random().toString(36).substring(7),
        role: "user",
        createdAt: new Date(),
      },
    ]);

    const messageHandler = async (e: MessageEvent) => {
      const data = JSON.parse(e.data);

      if (data.type === "error") {
        toast.info(data.data);
        setLoading(false);

        // Even when the request fails (guardrails block, throttling, etc.), the backend
        // still persists the user message + error response, so generate a session title
        // instead of leaving a new chat as "Untitled Conversation". Scoped to new
        // conversations so resuming an existing session is unaffected.
        if (isNewConversation && !titleGenerated) {
          titleGenerated = true;
          updateSessionTitle(currentSessionId);
        }

        ws?.removeEventListener("message", messageHandler);
        return;
      }

      if (data.type === "analyzing") {
        setAnalyzing(true);
      }

      if (data.type === "browsing") {
        setBrowsing(true);
      }

      if (data.type === "message") {
        // If we haven't yet added the new assistant message, do so once
        if (!added) {
          setMessages((prev) => [
            ...prev,
            {
              content: {
                content_type: "text",
                parts: data.data,
                meta: { files: [] },
              },
              id: data.message_id,
              role: "assistant",
              sources: sources,
              createdAt: new Date(),
            },
          ]);
          added = true;
        } else {
          // Update the last message reference (partial update)
          setMessages((prev) => {
            const newArr = [...prev];
            const idx = newArr.findIndex((m) => m.id === data.message_id);
            if (idx !== -1) {
              // mutate just that message
              const oldMsg = newArr[idx];
              newArr[idx] = {
                ...oldMsg,
                content: {
                  ...oldMsg.content,
                  parts: oldMsg.content.parts + data.data,
                },
              };
            }
            return newArr;
          });
        }
        receivedMessage += data.data;
        setMessageAppeared(true);
        setAnalyzing(false);
        setBrowsing(false);
      }

      if (data.type === "event" && data.data === "completion") {
        setChatHistory((prevHistory) => [
          ...prevHistory,
          ["user", message],
          ["assistant", receivedMessage],
        ]);
        ws?.removeEventListener("message", messageHandler);
        setLoading(false);

        // Generate the title once on the first completed turn of a new conversation.
        // We no longer gate on messagesRef.current.length === 2: agent/VA flows emit
        // extra intermediate messages and the ref can lag state, which used to leave
        // those chats stuck as "Untitled Conversation". `isNewConversation` preserves
        // the old behavior of not titling resumed sessions.
        if (isNewConversation && !titleGenerated) {
          titleGenerated = true;
          // Two passes, fired once: (1) an initial title right away for fast UI feedback,
          // then (2) once that finishes — by which point the model's answer is persisted —
          // a forced refresh so the final title reflects the full question + answer. The
          // second pass overwrites the first in DynamoDB and updates the sidebar UI.
          updateSessionTitle(currentSessionId).then(() =>
            updateSessionTitle(currentSessionId, true)
          );
        }
      }
    };

    ws?.addEventListener("message", messageHandler);
  };

  const stopMessageStream = () => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          type: "event",
          data: "stop_signal",
          session_id: sessionId,
        })
      );
      ws.close();
      setLoading(false);
    }
  };

  const rewrite = (message_id: string) => {
    const index = messages.findIndex((msg) => msg.id === message_id);
    if (index === -1) return;

    // The message to "re-send" is the user message right before it
    const userMsg = messages[index - 1];
    if (!userMsg) return;

    setMessages((prev) => [...prev.slice(0, Math.max(0, index - 1))]);
    setChatHistory((prev) => [...prev.slice(0, Math.max(0, index - 1))]);
    sendMessage(userMsg.content.parts);
  };

  const createVirtualAssistant = () => {
    setShowVACreator(true);
  };

  useEffect(() => {
    if (isReady && initialMessage) {
      sendMessage(initialMessage);
    }
  }, [isReady, initialMessage]);

  return isReady ? (
    <>
      <ChatLayout
        messages={messages}
        setIsExpanded={props.setIsExpanded}
        isExpanded={props.isExpanded}
        loading={loading}
        analyzing={analyzing}
        browsing={browsing}
        sendMessage={sendMessage}
        handleMic={handleMic}
        isRecording={recording}
        isSpeaking={isPlaying}
        stopMessageStream={stopMessageStream}
        messageAppeared={messageAppeared}
        rewrite={rewrite}
        focusMode={focusMode}
        setFocusMode={setFocusMode}
        createVirtualAssistant={createVirtualAssistant}
        clientLogoUrl={clientLogoUrl}
        logoLoading={logoLoading}
        logoError={logoError}
        fullConversationAudioUrl={fullAudioUrl}
      />

      <VoiceModeModal
        isOpen={showVoiceMode}
        onClose={closeVoiceMode}
        isRecording={recording}
        isMuted={muted}
        isSpeaking={isPlaying}
        isConnecting={connecting}
        isThinking={thinking}
        onToggleRecording={handleMic}
        onEndVoiceMode={closeVoiceMode}
        userText={userText}
        assistantText={assistantText}
        conversationHistory={conversationHistory}
        voiceBrainBank={voiceBrainBank}
      />

      {/* Virtual Assistant Creator Popup */}
      {showVACreator && (
        <div className="fixed inset-0 scrim flex items-center justify-center z-50">
          <div className="bg-surface-overlay rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ring-1 ring-black/5 dark:ring-gray-700">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-primary mb-2">
                Create Virtual Assistant
              </h3>
              <p className="text-muted">
                Would you like to create a Virtual Assistant based on this conversation?
                This will help you create a customized AI assistant using the context
                and knowledge from our current discussion.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowVACreator(false)}
                className="px-4 py-2 text-muted border border-border-strong rounded-md hover:bg-surface-2 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowVACreator(false);
                  setActiveConnector("virtual-assistant");
                  sendMessage("I would like to create a Virtual Assistant based on this conversation", [], "virtual-assistant");
                }}
                className="px-4 py-2 bg-violet-500 text-white rounded-md hover:bg-violet-600 transition-colors duration-200"
              >
                Create Virtual Assistant
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  ) : (
    <LoadingSpinner />
  );
};

export default ChatWindow;